import { useMemo } from 'react';
import { Tag, Typography, Space, Tooltip } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { useUserTimestampRender, useUserAvatarRender } from '@src/hooks';
import { MultilineTextField } from '@src/components/shared/fields';

const { Text } = Typography;

export const usePrescriptionColumns = (): SmartDataTableProps['columns'] => {
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });
  const userAvatarRender = useUserAvatarRender();

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        minWidth: 80,
        sorter: true,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'prescribed_at',
        title: 'Prescribed Date',
        dataIndex: 'prescribed_at',
        sorter: true,
        minWidth: 120,
        render: (value: string) => (
          <Text style={{ fontSize: '13px' }}>
            {value ? new Date(value).toLocaleDateString() : '-'}
          </Text>
        ),
      },
      {
        key: 'medicine_list',
        title: 'Medicine Details',
        dataIndex: 'prescription_medicines',
        minWidth: 300,
        render: (medicines: any) => {
          if (!medicines || medicines.length === 0) return '-';

          return (
            // <Tooltip title={tooltipContent}>
            <div style={{ cursor: 'pointer' }}>
              {medicines.map((med: any, index: number) => (
                <div
                  key={index}
                  style={{
                    marginBottom: index < medicines.length - 1 ? '4px' : '0',
                  }}
                >
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#262626',
                    }}
                  >
                    {med.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                    {med.dosage} • {med.when_take} • {med.qty}
                  </div>
                  {med.instruction && (
                    <div style={{ marginTop: '2px' }}>
                      <MultilineTextField
                        text={med.instruction}
                        fontSize="11px"
                        color="#8c8c8c"
                        fontWeight="normal"
                        lineHeight="1.3"
                        marginTop="0px"
                        marginBottom="0px"
                        showLabel={false}
                        textStyle={{ fontStyle: 'italic' }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            // </Tooltip>
          );
        },
      },
      {
        key: 'notes_info',
        title: 'Notes & Info',
        dataIndex: 'notes',
        minWidth: 200,
        render: (notes: string, record: any) => {
          return (
            <div>
              {notes && (
                <div style={{ marginBottom: '8px' }}>
                  <MultilineTextField
                    text={notes}
                    label="Notes"
                    fontSize="13px"
                    lineHeight="1.4"
                    marginTop="2px"
                    marginBottom="0px"
                  />
                </div>
              )}
              {record.lab_report && (
                <div style={{ marginBottom: '8px' }}>
                  <MultilineTextField
                    text={record.lab_report}
                    label="Lab Report"
                    fontSize="13px"
                    lineHeight="1.4"
                    marginTop="2px"
                    marginBottom="0px"
                  />
                </div>
              )}
              {record.today_procedure && (
                <div>
                  <MultilineTextField
                    text={record.today_procedure}
                    label="Procedure"
                    fontSize="13px"
                    lineHeight="1.4"
                    marginTop="2px"
                    marginBottom="0px"
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'doctor',
        title: 'Doctor',
        dataIndex: 'doctor',
        minWidth: 200,
        render: (doctor: any) => {
          if (!doctor) return '-';
          return userAvatarRender(doctor);
        },
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ],
    [userTimestampRender, userAvatarRender]
  );
};
